import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAnalyze } from "../hooks/useAnalyze";
import { MessageThread } from "../components/MessageThread";
import { AboutCard } from "../components/AboutCard";
import { DidYouKnowCard } from "../components/DidYouKnowCard";
import { CoachingCards } from "../components/CoachingCard";

const STEPS = [
  { emoji: "\uD83D\uDCAC", label: "Text\nnaturally" },
  { emoji: "\uD83E\uDDED", label: "Wren\ncoaches you" },
  { emoji: "\uD83D\uDC9A", label: "Communicate\nbetter" },
];

export function ResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { uid: paramUid, exampleId } = useParams<{ uid: string; exampleId: string }>();
  const file = (location.state as { file?: File } | null)?.file;
  const startedRef = useRef(false);
  const [dismissed, setDismissed] = useState(false);
  const [dotCount, setDotCount] = useState(0);

  const {
    messages,
    about,
    didYouKnow,
    coaching,
    isAnalyzing,
    error,
    uid,
    analyze,
    loadCached,
    reset,
  } = useAnalyze(paramUid);

  // Trigger analysis for file uploads or cached demos (once)
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (paramUid) return; // query handles permalink automatically
    if (exampleId) {
      loadCached(`/examples/${exampleId}.json`);
      return;
    }
    if (!file) {
      if (!forceLoading) navigate("/analyze", { replace: true });
      return;
    }
    analyze(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update URL when uid arrives from mutation (skip for demos — they stay on /demo/:id)
  useEffect(() => {
    if (uid && !paramUid && !exampleId) {
      window.history.replaceState(null, "", `/analyze/${uid}`);
    }
  }, [uid, paramUid, exampleId]);

  // Redirect to landing page if permalink fails (expired or not found)
  useEffect(() => {
    if (paramUid && error) {
      navigate("/analyze", { replace: true });
    }
  }, [paramUid, error, navigate]);

  const hasMessages = messages.length > 0;
  const hasResults = about || didYouKnow || coaching.length > 0;
  const forceLoading = new URLSearchParams(window.location.search).has("loading");
  const isDone = forceLoading ? false : !isAnalyzing && (hasMessages || hasResults);

  useEffect(() => {
    if (isDone) return;
    const id = setInterval(() => setDotCount((c) => (c + 1) % 4), 500);
    return () => clearInterval(id);
  }, [isDone]);

  // Skip interstitial for permalinks
  const showInterstitial = !paramUid && !dismissed;

  if (showInterstitial) {
    return (
      <div className="min-h-dvh bg-[#f5f3ef] flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full bg-[#2d2d2d] rounded-[24px] px-6 py-12 text-center">
          <p className="text-xs text-[#aaa] uppercase tracking-[2px] mb-4">
            While we analyze
          </p>
          <h2 className="font-serif text-[28px] text-white leading-tight mb-4">
            Want real-time analysis during your conversation?
          </h2>
          <p className="text-base text-[#999] leading-relaxed mb-8">
            Sign up for Wren with your partner to get real-time guidance before things escalate.
          </p>

          {/* Steps */}
          <div className="flex items-center justify-center gap-2 mb-12">
            {STEPS.map((step, i) => (
              <div key={step.label} className="flex items-center gap-2">
                <div className="flex flex-col items-center w-24">
                  <div className="w-14 h-14 rounded-2xl bg-[#3a3a3a] flex items-center justify-center text-2xl mb-2">
                    {step.emoji}
                  </div>
                  <p className="text-xs font-bold text-[#ccc] uppercase tracking-wider mb-0.5">
                    Step {i + 1}
                  </p>
                  <p className="text-xs text-[#999] leading-tight whitespace-pre-line">
                    {step.label}
                  </p>
                </div>
                {i < STEPS.length - 1 && (
                  <span className="text-[#555] text-sm mb-6">&middot;&middot;&middot;</span>
                )}
              </div>
            ))}
          </div>

          <p className="text-xs text-[#777] mb-8">
            This report is point-in-time. Wren helps in the moment.
          </p>

          {/* CTA button — dots while loading, solid when ready */}
          <button
            disabled={!isDone}
            onClick={() => setDismissed(true)}
            className="w-full py-4 rounded-2xl font-bold text-base"
            style={{
              background: isDone ? "#c2cfa0" : "#3a3a3a",
              color: isDone ? "#1a1a1a" : "#999",
              cursor: isDone ? "pointer" : "default",
            }}
          >
            {isDone ? (
              "See your snapshot \u2192"
            ) : (
              <span>
                Analyzing snapshot
                <span className="inline-block w-[1.5em] text-left">
                  {".".repeat(dotCount)}
                </span>
              </span>
            )}
          </button>

          {/* Error inline */}
          {error && (
            <div className="mt-4 p-4 rounded-xl bg-red-900/30 text-sm text-red-300 text-left">
              <p>{error}</p>
              <button
                onClick={() => { reset(); navigate("/analyze"); }}
                className="mt-2 text-white font-medium text-sm underline"
              >
                Try again
              </button>
            </div>
          )}

          <a
            href="https://chatwren.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-xs text-[#777] mt-4 hover:text-[#aaa] transition-colors"
          >
            Learn more
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-[#f5f3ef] flex flex-col">
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-md mx-auto w-full px-4 py-6">
          {/* Dark hero header — contained within content pane */}
          <div className="bg-[#2d2d2d] rounded-2xl px-6 pt-12 pb-8 mb-6 text-center">
            {isDone && (
              <div className="animate-analyze-fade-in mb-4">
                <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#ccc] bg-[#3a3a3a] px-4 py-2 rounded-full uppercase tracking-wider">
                  <span className="w-2 h-2 rounded-full bg-[#638308]" />
                  Analysis complete
                </span>
              </div>
            )}

            <h1 className="font-serif text-3xl text-white">
              Here{"\u2019"}s what{"\u2019"}s happening
            </h1>
          </div>

          {/* Error */}
          {error && (
            <div className="mt-4 p-4 rounded-2xl bg-red-50 text-sm text-red-600">
              <p className="font-medium mb-1">Something went wrong</p>
              <p>{error}</p>
              <button
                onClick={() => { reset(); navigate("/analyze"); }}
                className="mt-4 text-[#638308] font-medium text-sm"
              >
                Try again
              </button>
            </div>
          )}

          {/* Message thread */}
          {hasMessages && (
            <div className="mb-6">
              <MessageThread messages={messages} />
            </div>
          )}

          {/* About card with section header */}
          {about && (
            <div className="animate-analyze-cascade" style={{ animationDelay: "0ms" }}>
              <AboutCard data={about} />
            </div>
          )}

          {/* Did you know — between about and coaching */}
          {didYouKnow && (
            <div className="animate-analyze-cascade mt-6" style={{ animationDelay: "150ms" }}>
              <DidYouKnowCard data={didYouKnow} />
            </div>
          )}

          {/* Coaching section */}
          {coaching.length > 0 && (
            <div className="animate-analyze-cascade mt-6" style={{ animationDelay: "300ms" }}>
              <h3 className="text-xs font-semibold text-[#638308] uppercase tracking-wider px-1 mb-4">
                What to try next time
              </h3>
              <CoachingCards items={coaching} />
            </div>
          )}

          {/* CTA footer */}
          {isDone && (
            <div className="animate-analyze-cascade" style={{ animationDelay: "450ms" }}>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
